import React, { useEffect, memo, useRef } from "react";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import { makeStyles } from "@material-ui/core/styles";
import campusData from "../../../json/campus_data.json";
import {
  localizedField,
  t,
  useLanguage,
} from "../../../contexts/LanguageContext";
import { useSnackbar } from "notistack";
import SnackbarContent from "@material-ui/core/SnackbarContent";

const useStyles = makeStyles((theme) => ({
  snackbarContent: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: "0.7rem",
  },
  actionButton: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: "0.8rem",
  },
  message: {
    display: "flex",
    alignItems: "center",
  },
  messageIcon: {
    marginRight: theme.spacing(0.5),
  },
}));

function NearestCampusSnackbar({ open, onClose, onConfirm, nearestCampus }) {
  const classes = useStyles();
  const { language } = useLanguage();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const snackbarKeyRef = useRef(null);
  const campus = (campusData?.campusList ?? [])[nearestCampus - 1];
  const campusLabel = localizedField(campus, "label", language) || "";
  const message = t("moveToNearestCampusPrompt", language).replace(
    "{campusLabel}",
    campusLabel,
  );
  const confirmLabel = t("moveToNearestCampusConfirm", language);

  useEffect(() => {
    if (open && !snackbarKeyRef.current) {
      const key = enqueueSnackbar(message, {
          autoHideDuration: 10000,
          content: (snackbarKey) => (
            <SnackbarContent
              className={classes.snackbarContent}
              message={
                <span className={classes.message}>
                  <ErrorOutlineIcon
                    className={classes.messageIcon}
                    fontSize="small"
                  />
                  {message}
                </span>
              }
              action={
                <>
                  <Button
                    className={classes.actionButton}
                    size="small"
                    onClick={() => {
                      onConfirm();
                      closeSnackbar(snackbarKey);
                    }}
                  >
                    {confirmLabel}
                  </Button>
                  <IconButton
                    size="small"
                    aria-label="close"
                    color="inherit"
                    onClick={() => closeSnackbar(snackbarKey)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </>
              }
            />
          ),
          onClose: () => {
            snackbarKeyRef.current = null;
            onClose();
          },
        });
      snackbarKeyRef.current = key;
    }

    if (!open && snackbarKeyRef.current) {
      closeSnackbar(snackbarKeyRef.current);
      snackbarKeyRef.current = null;
    }
  }, [
    open,
    message,
    confirmLabel,
    enqueueSnackbar,
    closeSnackbar,
    classes.snackbarContent,
    classes.actionButton,
    classes.message,
    classes.messageIcon,
    onClose,
    onConfirm,
  ]);

  return null;
}

export default memo(NearestCampusSnackbar);
